import { useCallback, useMemo } from 'react'
import { assign, setup, type SnapshotFrom } from 'xstate'

import { useActorRef, useSelector } from '@xstate/react'
import { isChatToolLoadingKey, type ChatToolLoadingKey } from 'tripPlanner/config/chatToolLoading'
import { EmptyArray } from 'lib/array/arrayUtils'
import type { AssistantOfferPageTurnAuthority } from 'tripPlanner/assistant/api/types'

export const EMPTY_SUMMARIES: ReadonlyArray<string> = []

// ── Loading State Types (migrated from useChatLoadingState) ───────────

export type ChatLoadingStatus =
  | 'idle'
  | 'loadingHistory'
  | 'preparingStream'
  | 'reasoning'
  | 'toolCalling'
  | 'streaming'
  | 'updatingItinerary'
  | 'retrying'
  | 'reconnecting'
  | 'error'

export interface ChatLoadingState {
  status: ChatLoadingStatus
  isListLoading: boolean
  isUpdatingItinerary: boolean
  /** True when content is actively streaming or the itinerary is being updated.
   *  False during preparingStream (no markers arriving yet) and after
   *  CONTENT_COMPLETE (post-stream enrichment is tracked separately). */
  isContentStreaming: boolean
  activeToolLoadingKey?: ChatToolLoadingKey
  errorMessage?: string
}

export function isStreamingStatus(status: ChatLoadingStatus): boolean {
  return status === 'preparingStream' || status === 'reasoning' || status === 'toolCalling' || status === 'streaming' || status === 'updatingItinerary' || status === 'retrying' || status === 'reconnecting'
}

// ── Machine Types ─────────────────────────────────────────────────────

export interface StreamMachineContext {
  // Stream lifecycle
  generation: number
  chatId: number | undefined
  userMessageId: string | undefined
  assistantMessageId: string | undefined
  abortController: AbortController | undefined
  contentCompleteReceived: boolean
  hasReceivedFirstChunk: boolean
  retryAttempt: number
  error: Error | undefined
  // History loading (orthogonal to streaming)
  isLoadingHistory: boolean
  historyErrorMessage: string | undefined
  // Tool loading (active while connecting, or between assistant text chunks)
  activeToolLoadingCalls: Array<{ callId: string; key: string }>
  // Itinerary update tracking
  isUpdatingItinerary: boolean
  // Status event data — displayed by loading indicator while the model is thinking or picking tools
  hasReasoningStarted: boolean
  isAwaitingModelResponse: boolean
  reasoningSummaries: ReadonlyArray<string>
  toolCallInfo: { tool: string; args: Record<string, unknown> } | undefined
  // v3 SSE cursor (connection lifecycle managed via streamCloseRef in useStreamConnection)
  cursor: string
  // Turn authority accepted for the current/last connected stream, resolved via the
  // platform's snapshot-authority contract. Captured so an inline retry of the same
  // (chatId, userMessageId, assistantMessageId) pair can replay it exactly instead of
  // re-resolving — resolving fresh on retry would violate that contract.
  turnAuthority: AssistantOfferPageTurnAuthority | undefined
}

export type StreamMachineEvent =
  | { type: 'SUBMIT'; generation: number }
  | { type: 'REGENERATE'; generation: number }
  | { type: 'RESUME'; generation: number; chatId: number; assistantMessageId: string }
  | { type: 'CONNECTED'; chatId: number; userMessageId: string | undefined; assistantMessageId: string; abortController: AbortController; turnAuthority?: AssistantOfferPageTurnAuthority }
  | { type: 'FIRST_CHUNK' }
  | { type: 'CONTENT_COMPLETE' }
  | { type: 'MESSAGE_END' }
  | { type: 'STREAM_END' }
  | { type: 'STOP' }
  | { type: 'RETRY'; attempt: number }
  | { type: 'ERROR'; error: Error }
  | { type: 'RESET' }
  | { type: 'RECONNECTING' }
  | { type: 'RECONNECTED' }
  | { type: 'RECOVERY_START' }
  | { type: 'LOAD_HISTORY_START' }
  | { type: 'LOAD_HISTORY_SUCCESS' }
  | { type: 'LOAD_HISTORY_ERROR'; message: string }
  | { type: 'TOOL_LOADING_START'; callId: string; key: string }
  | { type: 'TOOL_LOADING_COMPLETE'; callId: string }
  | { type: 'ITINERARY_UPDATE_START' }
  | { type: 'ITINERARY_UPDATE_COMPLETE' }
  | { type: 'STATUS'; phase: 'reasoning'; summaries?: ReadonlyArray<string> }
  | { type: 'STATUS'; phase: 'tool_calling'; tool: string; args: Record<string, unknown> }
  | { type: 'HEARTBEAT' }
  | { type: 'CURSOR_UPDATE'; cursor: string }

type MachineSnapshot = SnapshotFrom<typeof streamMachine>

export type StreamPhase = MachineSnapshot['value']

// ── Constants ─────────────────────────────────────────────────────────

/** Time before the connecting state auto-errors with a timeout. */
const CONNECTION_TIMEOUT_MS = 15_000

// ── Machine ───────────────────────────────────────────────────────────

const INITIAL_PROGRESS = {
  contentCompleteReceived: false,
  hasReceivedFirstChunk: false,
  activeToolLoadingCalls: EmptyArray,
  isUpdatingItinerary: false,
  hasReasoningStarted: false,
  isAwaitingModelResponse: false,
  reasoningSummaries: EMPTY_SUMMARIES,
  toolCallInfo: undefined,
}

const INITIAL_CONTEXT: StreamMachineContext = {
  ...INITIAL_PROGRESS,
  generation: 0,
  chatId: undefined,
  userMessageId: undefined,
  assistantMessageId: undefined,
  abortController: undefined,
  retryAttempt: 0,
  error: undefined,
  isLoadingHistory: false,
  historyErrorMessage: undefined,
  cursor: '0',
  turnAuthority: undefined,
}

export const streamMachine = setup({
  types: {
    context: {} as StreamMachineContext,
    events: {} as StreamMachineEvent,
  },
  actions: {
    captureConnection: assign(({ event }) => {
      if (event.type !== 'CONNECTED') return {}
      return {
        chatId: event.chatId,
        userMessageId: event.userMessageId,
        assistantMessageId: event.assistantMessageId,
        abortController: event.abortController,
        turnAuthority: event.turnAuthority,
      }
    }),
    resetRecoveryProgress: assign(INITIAL_PROGRESS),
    markFirstChunk: assign({
      hasReceivedFirstChunk: true,
      isUpdatingItinerary: false,
    }),
    markContentComplete: assign({
      contentCompleteReceived: true,
      isAwaitingModelResponse: false,
    }),
    captureError: assign(({ event }) => ({
      error: event.type === 'ERROR' ? event.error : undefined,
    })),
    captureRetry: assign(({ event }) => ({
      retryAttempt: event.type === 'RETRY' ? event.attempt : 0,
      // Clear tool loading and itinerary updating on retry (matches reducer RETRY)
      activeToolLoadingCalls: EmptyArray,
      isUpdatingItinerary: false,
      isAwaitingModelResponse: false,
    })),
    closeStreamConnection: ({ context }) => {
      context.abortController?.abort()
    },
    captureResume: assign(({ event }) => {
      if (event.type !== 'RESUME') return {}
      return {
        generation: event.generation,
        chatId: event.chatId,
        assistantMessageId: event.assistantMessageId,
      }
    }),
    updateCursor: assign(({ event }) => {
      if (event.type !== 'CURSOR_UPDATE') return {}
      return { cursor: event.cursor }
    }),
    resetForNewStream: assign(({ event }) => ({
      ...INITIAL_PROGRESS,
      generation: 'generation' in event && typeof event.generation === 'number' ? event.generation : 0,
      userMessageId: undefined,
      assistantMessageId: undefined,
      abortController: undefined,
      retryAttempt: 0,
      error: undefined,
      historyErrorMessage: undefined,
      cursor: '0',
      // Reset defensively even though CONNECTED always overwrites this for the
      // new stream — a stale authority from a prior stream must never be read
      // by retryLastMessage before this stream's own CONNECTED fires.
      turnAuthority: undefined,
    })),
    captureStatus: assign(({ context, event }) => {
      if (event.type !== 'STATUS') return {}
      if (event.phase === 'reasoning') {
        let reasoningSummaries = context.reasoningSummaries
        if (event.summaries?.length && (
          event.summaries.length !== reasoningSummaries.length ||
          event.summaries.some((summary, index) => summary !== reasoningSummaries[index])
        )) {
          reasoningSummaries = event.summaries
        }
        return {
          hasReasoningStarted: true,
          isAwaitingModelResponse: true,
          reasoningSummaries,
          toolCallInfo: undefined,
        }
      }
      if (event.phase === 'tool_calling') {
        return {
          toolCallInfo: { tool: event.tool, args: event.args },
        }
      }
      return {}
    }),
    resetAll: assign(INITIAL_CONTEXT),
    // History loading
    startLoadingHistory: assign({
      isLoadingHistory: true,
      historyErrorMessage: undefined,
    }),
    finishLoadingHistory: assign({
      isLoadingHistory: false,
    }),
    failLoadingHistory: assign(({ event }) => ({
      isLoadingHistory: false,
      historyErrorMessage: event.type === 'LOAD_HISTORY_ERROR' ? event.message : undefined,
    })),
    // Tool loading
    addToolLoading: assign(({ context, event }) => {
      if (event.type !== 'TOOL_LOADING_START') return {}
      return {
        activeToolLoadingCalls: [
          ...context.activeToolLoadingCalls.filter(tc => tc.callId !== event.callId),
          { callId: event.callId, key: event.key },
        ],
        isAwaitingModelResponse: false,
        toolCallInfo: undefined,
      }
    }),
    removeToolLoading: assign(({ context, event }) => {
      if (event.type !== 'TOOL_LOADING_COMPLETE') return {}
      const activeToolLoadingCalls = context.activeToolLoadingCalls.filter(tc => tc.callId !== event.callId)
      return {
        activeToolLoadingCalls,
        isAwaitingModelResponse: activeToolLoadingCalls.length === 0,
        toolCallInfo: undefined,
      }
    }),
    // Itinerary updates
    startItineraryUpdate: assign({
      isUpdatingItinerary: true,
      isAwaitingModelResponse: false,
    }),
    finishItineraryUpdate: assign({
      isUpdatingItinerary: false,
    }),
    // Connection timeout
    captureTimeout: assign({
      error: () => new Error('Connection timed out'),
    }),
  },
}).createMachine({
  id: 'chatStream',
  initial: 'idle',
  context: INITIAL_CONTEXT,
  states: {
    idle: {
      on: {
        SUBMIT: { target: 'connecting', actions: 'resetForNewStream' },
        REGENERATE: { target: 'connecting', actions: 'resetForNewStream' },
        RESUME: { target: 'connecting', actions: 'captureResume' },
        RESET: { actions: 'resetAll' },
        LOAD_HISTORY_START: { actions: 'startLoadingHistory' },
        LOAD_HISTORY_SUCCESS: { actions: 'finishLoadingHistory' },
        LOAD_HISTORY_ERROR: { actions: 'failLoadingHistory' },
        ITINERARY_UPDATE_START: { actions: 'startItineraryUpdate' },
        ITINERARY_UPDATE_COMPLETE: { actions: 'finishItineraryUpdate' },
      },
    },
    connecting: {
      after: {
        [CONNECTION_TIMEOUT_MS]: { target: 'error', actions: 'captureTimeout' },
      },
      on: {
        RECOVERY_START: { target: 'connecting', actions: 'resetRecoveryProgress' },
        CONTENT_COMPLETE: { target: 'receiving', actions: 'markContentComplete' },
        MESSAGE_END: { target: 'finalizing' },
        CONNECTED: { actions: 'captureConnection' },
        FIRST_CHUNK: { target: 'receiving', actions: 'markFirstChunk' },
        RETRY: { target: 'retrying', actions: 'captureRetry' },
        ERROR: { target: 'error', actions: 'captureError' },
        STOP: { target: 'stopping' },
        STREAM_END: { target: 'idle', actions: 'resetAll' },
        RESET: { target: 'idle', actions: ['closeStreamConnection', 'resetAll'] },
        // Server activity restarts the connection timeout. reenter: true re-enters
        // connecting, which cancels and restarts the after delay. Without this, a
        // message that spends >15s on tool calls before producing text would timeout
        // despite the server actively responding.
        TOOL_LOADING_START: { target: 'connecting', reenter: true, actions: 'addToolLoading' },
        TOOL_LOADING_COMPLETE: { target: 'connecting', reenter: true, actions: 'removeToolLoading' },
        ITINERARY_UPDATE_START: { target: 'connecting', reenter: true, actions: 'startItineraryUpdate' },
        ITINERARY_UPDATE_COMPLETE: { target: 'connecting', reenter: true, actions: 'finishItineraryUpdate' },
        // History loading is orthogonal to streaming — allow it to complete so
        // isLoadingHistory doesn't get stuck true after the stream finishes.
        LOAD_HISTORY_SUCCESS: { actions: 'finishLoadingHistory' },
        LOAD_HISTORY_ERROR: { actions: 'failLoadingHistory' },
        // Status events provide phase-specific loading feedback. reenter: true restarts
        // the connection timeout (same pattern as TOOL_LOADING and ITINERARY_UPDATE).
        STATUS: { target: 'connecting', reenter: true, actions: 'captureStatus' },
        HEARTBEAT: { target: 'connecting', reenter: true },
      },
    },
    receiving: {
      on: {
        RECOVERY_START: { target: 'connecting', actions: 'resetRecoveryProgress' },
        SUBMIT: { target: 'connecting', actions: ['closeStreamConnection', 'resetForNewStream'] },
        REGENERATE: { target: 'connecting', actions: ['closeStreamConnection', 'resetForNewStream'] },
        CONTENT_COMPLETE: { actions: 'markContentComplete' },
        MESSAGE_END: { target: 'finalizing' },
        TOOL_LOADING_START: { actions: 'addToolLoading' },
        TOOL_LOADING_COMPLETE: { actions: 'removeToolLoading' },
        STATUS: { actions: 'captureStatus' },
        RETRY: { target: 'retrying', actions: 'captureRetry' },
        ERROR: { target: 'error', actions: 'captureError' },
        STOP: { target: 'stopping' },
        RECONNECTING: { target: 'reconnecting' },
        STREAM_END: { target: 'idle', actions: 'resetAll' },
        RESET: { target: 'idle', actions: ['closeStreamConnection', 'resetAll'] },
        CURSOR_UPDATE: { actions: 'updateCursor' },
        LOAD_HISTORY_SUCCESS: { actions: 'finishLoadingHistory' },
        LOAD_HISTORY_ERROR: { actions: 'failLoadingHistory' },
        // ITINERARY_UPDATE_START / ITINERARY_UPDATE_COMPLETE: intentionally unhandled during
        // streaming. markFirstChunk already clears isUpdatingItinerary on entry to receiving,
        // so a late COMPLETE would be a no-op. START is also dropped to avoid showing
        // "itinerary updating" while content is actively streaming.
      },
    },
    finalizing: {
      on: {
        RECOVERY_START: { target: 'connecting', actions: 'resetRecoveryProgress' },
        SUBMIT: { target: 'connecting', actions: ['closeStreamConnection', 'resetForNewStream'] },
        REGENERATE: { target: 'connecting', actions: ['closeStreamConnection', 'resetForNewStream'] },
        STREAM_END: { target: 'idle', actions: 'resetAll' },
        ERROR: { target: 'error', actions: 'captureError' },
        RESET: { target: 'idle', actions: ['closeStreamConnection', 'resetAll'] },
      },
    },
    retrying: {
      on: {
        RETRY: { actions: 'captureRetry' },
        FIRST_CHUNK: { target: 'receiving', actions: 'markFirstChunk' },
        ERROR: { target: 'error', actions: 'captureError' },
        STOP: { target: 'stopping' },
        STREAM_END: { target: 'idle', actions: 'resetAll' },
        RESET: { target: 'idle', actions: ['closeStreamConnection', 'resetAll'] },
      },
    },
    stopping: {
      entry: 'closeStreamConnection',
      on: {
        STREAM_END: { target: 'idle', actions: 'resetAll' },
        // ERROR can arrive if a network error fires at roughly the same time as
        // user stop — executeStream's finally sends ERROR instead of STREAM_END.
        // The error is intentionally swallowed (user initiated stop, so the
        // concurrent error is not surfaced). Telemetry is still emitted by
        // handleSubmit/handleRetry's catch block before the machine event is sent.
        ERROR: { target: 'idle', actions: 'resetAll' },
        RESET: { target: 'idle', actions: 'resetAll' },
      },
    },
    reconnecting: {
      on: {
        RECOVERY_START: { target: 'connecting', actions: 'resetRecoveryProgress' },
        RECONNECTED: { target: 'receiving' },
        STREAM_END: { target: 'idle', actions: 'resetAll' },
        ERROR: { target: 'error', actions: 'captureError' },
        STOP: { target: 'stopping' },
        RESET: { target: 'idle', actions: ['closeStreamConnection', 'resetAll'] },
      },
    },
    error: {
      entry: 'closeStreamConnection',
      on: {
        SUBMIT: { target: 'connecting', actions: 'resetForNewStream' },
        REGENERATE: { target: 'connecting', actions: 'resetForNewStream' },
        RETRY: { target: 'retrying', actions: 'captureRetry' },
        STREAM_END: { target: 'idle', actions: 'resetAll' },
        RESET: { target: 'idle', actions: 'resetAll' },
        LOAD_HISTORY_START: { actions: 'startLoadingHistory' },
        LOAD_HISTORY_SUCCESS: { actions: 'finishLoadingHistory' },
        LOAD_HISTORY_ERROR: { actions: 'failLoadingHistory' },
      },
    },
  },
})

// ── Loading State Derivation ──────────────────────────────────────────

/** Subset of machine context fields used by derivation functions and the hook.
 *  Compared field-by-field in the selector to avoid re-renders from
 *  high-frequency events that only touch other context fields. */
export interface StreamContextSlice {
  contentCompleteReceived: boolean
  isLoadingHistory: boolean
  historyErrorMessage: string | undefined
  activeToolLoadingCalls: Array<{ callId: string; key: string }>
  hasReasoningStarted: boolean
  reasoningSummaries: ReadonlyArray<string>
  toolCallInfo: { tool: string; args: Record<string, unknown> } | undefined
  isAwaitingModelResponse: boolean
  isUpdatingItinerary: boolean
  error: Error | undefined
  chatId: number | undefined
  userMessageId: string | undefined
  assistantMessageId: string | undefined
}

export function deriveChatLoadingStatus(
  phase: StreamPhase,
  context: StreamContextSlice,
): ChatLoadingStatus {
  switch (phase) {
    case 'idle':
      if (context.historyErrorMessage) return 'error'
      if (context.isLoadingHistory) return 'loadingHistory'
      if (context.isUpdatingItinerary) return 'updatingItinerary'
      return 'idle'
    case 'connecting':
      if (context.contentCompleteReceived) return 'idle'
      if (context.activeToolLoadingCalls.length > 0) return 'toolCalling'
      if (context.toolCallInfo !== undefined) return 'toolCalling'
      if (context.hasReasoningStarted) return 'reasoning'
      if (context.isUpdatingItinerary) return 'updatingItinerary'
      return 'preparingStream'
    case 'receiving':
      // Critical: matches reducer's STREAM_COMPLETE → idle. Consumers depend on
      // isStreaming being false after content_complete, even though the protocol
      // stream is still open (waiting for MESSAGE_END → STREAM_END).
      if (context.contentCompleteReceived) return 'idle'
      if (context.activeToolLoadingCalls.length > 0) return 'toolCalling'
      if (context.isAwaitingModelResponse) return 'reasoning'
      return 'streaming'
    case 'finalizing':
      return 'idle'
    case 'retrying':
      return context.contentCompleteReceived ? 'idle' : 'retrying'
    case 'stopping':
      return 'idle'
    case 'reconnecting':
      return context.contentCompleteReceived ? 'idle' : 'reconnecting'
    case 'error':
      return 'error'
  }
}

export function deriveChatLoadingState(
  phase: StreamPhase,
  context: StreamContextSlice,
): ChatLoadingState {
  const status = deriveChatLoadingStatus(phase, context)
  const isUpdatingItinerary = status === 'updatingItinerary'
  const isContentStreaming = status === 'streaming' || status === 'reasoning' || isUpdatingItinerary
  const isListLoading = isStreamingStatus(status)

  const latestVisibleToolLoadingKey =
    status === 'toolCalling' ? context.activeToolLoadingCalls.at(-1)?.key : undefined

  return {
    status,
    isListLoading,
    isUpdatingItinerary,
    isContentStreaming,
    activeToolLoadingKey: isChatToolLoadingKey(latestVisibleToolLoadingKey)
      ? latestVisibleToolLoadingKey
      : undefined,
    errorMessage: context.historyErrorMessage ?? (phase === 'error' ? context.error?.message : undefined),
  }
}

// ── Selector equality ─────────────────────────────────────────────────

function selectContextSlice(snapshot: SnapshotFrom<typeof streamMachine>): StreamContextSlice {
  const ctx = snapshot.context
  return {
    contentCompleteReceived: ctx.contentCompleteReceived,
    isLoadingHistory: ctx.isLoadingHistory,
    historyErrorMessage: ctx.historyErrorMessage,
    activeToolLoadingCalls: ctx.activeToolLoadingCalls,
    isUpdatingItinerary: ctx.isUpdatingItinerary,
    hasReasoningStarted: ctx.hasReasoningStarted,
    isAwaitingModelResponse: ctx.isAwaitingModelResponse,
    reasoningSummaries: ctx.reasoningSummaries,
    toolCallInfo: ctx.toolCallInfo,
    error: ctx.error,
    chatId: ctx.chatId,
    userMessageId: ctx.userMessageId,
    assistantMessageId: ctx.assistantMessageId,
  }
}

function contextSliceEqual(a: StreamContextSlice, b: StreamContextSlice): boolean {
  return (
    a.contentCompleteReceived === b.contentCompleteReceived &&
    a.isLoadingHistory === b.isLoadingHistory &&
    a.historyErrorMessage === b.historyErrorMessage &&
    a.activeToolLoadingCalls === b.activeToolLoadingCalls &&
    a.isUpdatingItinerary === b.isUpdatingItinerary &&
    a.hasReasoningStarted === b.hasReasoningStarted &&
    a.isAwaitingModelResponse === b.isAwaitingModelResponse &&
    a.reasoningSummaries === b.reasoningSummaries &&
    a.toolCallInfo === b.toolCallInfo &&
    a.error === b.error &&
    a.chatId === b.chatId &&
    a.userMessageId === b.userMessageId &&
    a.assistantMessageId === b.assistantMessageId
  )
}

// ── Hook ──────────────────────────────────────────────────────────────

export function useStreamMachine() {
  const actorRef = useActorRef(streamMachine)

  const phase = useSelector(actorRef, (s) => s.value)
  const ctx = useSelector(actorRef, selectContextSlice, contextSliceEqual)

  const loadingState = useMemo(
    () => deriveChatLoadingState(phase, ctx),
    [phase, ctx],
  )

  const getSnapshot = useCallback(() => {
    const s = actorRef.getSnapshot()
    return { phase: s.value, context: s.context }
  }, [actorRef])

  return useMemo(() => ({
    send: actorRef.send,
    getSnapshot,
    phase,
    context: ctx,
    loadingState,
    isStreamOpen: phase === 'connecting' || phase === 'receiving' || phase === 'finalizing' || phase === 'retrying' || phase === 'reconnecting',
    isAwaitingMessageEnd: phase === 'receiving',
    isStreaming: isStreamingStatus(loadingState.status),
    isRetrying: phase === 'retrying',
  }), [actorRef.send, getSnapshot, phase, ctx, loadingState])
}
